import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import {
  TrendingUp, Euro, Wallet, FileText, AlertTriangle, Calendar,
  Clock, CheckCircle2, ArrowRight, Receipt,
} from 'lucide-react';
import { api } from '../lib/api';
import { Card } from './ui/card';
import { Badge } from './ui/badge';

const fmtEUR = (v) => new Intl.NumberFormat('it-IT', {
  style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0,
}).format(Number(v || 0));

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return '—'; }
};

/**
 * ★ 2026-08-14 — MonthlyIncomeCard
 *
 * Widget dedicato "Incasso del Mese" visibile a TUTTI gli agenti
 * (Associate, Senior, CEO, Co-CEO, Admin Staff). Mostra:
 *  1. Incasso del mese corrente (Maturato + Effettivamente incassato)
 *  2. Fatture in scadenza (countdown 15gg dopo incasso)
 *  3. Storico ultimi 12 mesi (mini-tabella scrollabile)
 *
 * Polling 90s + refresh su visibilitychange.
 */
export default function MonthlyIncomeCard() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showHistory, setShowHistory] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get('/agent-commissions/monthly-dashboard');
      setData(d);
    } catch (e) {
      /* silent — potrebbe non essere agente / no auth */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 90_000);
    const onVis = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', onVis); };
  }, [load]);

  const historyChartMax = useMemo(() => {
    if (!data?.history) return 0;
    return data.history.reduce(
      (max, h) => Math.max(max, h.matured_total || 0, h.collected_total || 0),
      0,
    );
  }, [data]);

  if (loading) {
    return (
      <Card className="p-5 md:p-6 animate-pulse" data-testid="monthly-income-card-loading">
        <div className="h-4 w-40 bg-[var(--surface-2)] rounded mb-3" />
        <div className="h-10 w-32 bg-[var(--surface-2)] rounded" />
      </Card>
    );
  }

  if (!data) return null;

  const cm = data.current_month || {};
  const deadlines = data.invoice_deadlines || [];
  const urgentCount = deadlines.filter((d) => d.is_urgent || d.is_overdue).length;

  return (
    <Card className="p-4 md:p-6" data-testid="monthly-income-card">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground inline-flex items-center gap-1.5">
            <Wallet className="w-3 h-3 text-[var(--gp-gold)]" />
            {t('income.monthIncome', 'Incasso del mese')}
          </div>
          <h3 className="font-serif text-xl md:text-2xl mt-1">
            {cm.label || '—'}
          </h3>
        </div>
        {urgentCount > 0 && (
          <Badge variant="destructive" className="bg-red-100 text-red-800 border-red-300" data-testid="urgent-invoices-badge">
            <AlertTriangle className="w-3 h-3 mr-1" />
            {urgentCount} {t('income.urgentInvoices', 'fatture urgenti')}
          </Badge>
        )}
      </div>

      {/* KPI grid: Maturato + Incassato */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* Maturato */}
        <div
          className="rounded-xl bg-gradient-to-br from-[var(--gp-gold)]/12 via-[var(--gp-gold)]/6 to-transparent border border-[var(--gp-gold)]/25 p-4"
          data-testid="matured-block"
        >
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground inline-flex items-center gap-1">
            <TrendingUp className="w-3 h-3" />
            {t('income.maturedMonth', 'Maturato mese')}
          </div>
          <div
            className="font-serif text-3xl md:text-4xl text-[var(--gp-gold)] tabular-nums mt-1"
            data-testid="matured-total"
          >
            {fmtEUR(cm.matured_total)}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">
            {cm.matured_count || 0} {cm.matured_count === 1 ? t('income.maturedOne', 'commissione maturata') : t('income.maturedMany', 'commissioni maturate')}
          </div>
          <div className="text-[10px] text-muted-foreground mt-1 italic">
            {t('income.maturedHint', 'Include tutte le commissioni con sblocco nel mese, indipendentemente dallo stato.')}
          </div>
        </div>

        {/* Effettivamente incassato */}
        <div
          className="rounded-xl border border-emerald-200 p-4 bg-gradient-to-br from-emerald-50/60 to-white"
          data-testid="collected-block"
        >
          <div className="text-[10px] uppercase tracking-widest text-emerald-700 inline-flex items-center gap-1">
            <Euro className="w-3 h-3" />
            Effettivamente incassato
          </div>
          <div
            className="font-serif text-3xl md:text-4xl text-emerald-800 tabular-nums mt-1"
            data-testid="collected-total"
          >
            {fmtEUR(cm.collected_total)}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">
            {cm.collected_count || 0} {cm.collected_count === 1 ? t('income.paidOne', 'commissione pagata') : t('income.paidMany', 'commissioni pagate')}
          </div>
          <div className="text-[10px] text-muted-foreground mt-1 italic">
            {t('income.paidHint', 'Commissioni effettivamente pagate da HCF Group nel mese corrente.')}
          </div>
        </div>
      </div>

      {/* Fatture da emettere */}
      <div className="mt-5">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground inline-flex items-center gap-1.5">
            <Receipt className="w-3 h-3 text-[var(--gp-gold)]" />
            {t('income.invoicesToIssue', 'Fatture da emettere')} ({data.invoice_window_days || 15}{t('income.daysFromCollection', 'gg dall\'incasso')})
          </div>
          <Badge variant="outline" className="text-[10px] py-0 h-5">
            {deadlines.length} {t('income.toInvoice', 'da fatturare')}
          </Badge>
        </div>

        {deadlines.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border p-4 text-center text-xs text-muted-foreground bg-[var(--surface-2)]/30">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 mx-auto mb-1.5" />
            {t('income.noDeadlines', 'Nessuna fattura in scadenza. Continua così!')}
          </div>
        ) : (
          <div className="space-y-2" data-testid="invoice-deadlines-list">
            {deadlines.slice(0, 4).map((d) => (
              <InvoiceDeadlineRow key={d.id} row={d} />
            ))}
            {deadlines.length > 4 && (
              <div className="text-[11px] text-muted-foreground text-center pt-1">
                + {deadlines.length - 4} {t('income.moreDeadlines', 'altre fatture in scadenza — vedi')}{' '}
                <Link
                  to="/app/my-commissions"
                  className="text-[hsl(var(--primary))] hover:underline"
                >
                  {t('income.fullList', 'la lista completa')}
                </Link>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Storico ultimi 12 mesi (toggle) */}
      <div className="mt-5 pt-4 border-t border-border">
        <button
          type="button"
          onClick={() => setShowHistory((s) => !s)}
          className="text-[11px] uppercase tracking-widest text-muted-foreground inline-flex items-center gap-1.5 hover:text-foreground transition-colors"
          data-testid="toggle-history"
        >
          <Calendar className="w-3 h-3" />
          {t('income.history12m', 'Storico ultimi 12 mesi')}
          <span className="ml-1 text-[10px]">{showHistory ? '▲' : '▼'}</span>
        </button>

        {showHistory && (
          <div className="mt-3 overflow-x-auto" data-testid="history-panel">
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="text-left py-2 px-2">Mese</th>
                  <th className="text-right py-2 px-2">Maturato</th>
                  <th className="text-right py-2 px-2">Incassato</th>
                  <th className="text-right py-2 px-2">N°</th>
                </tr>
              </thead>
              <tbody>
                {(data.history || []).map((h) => {
                  const barPct = historyChartMax > 0
                    ? Math.max(2, Math.round((h.matured_total / historyChartMax) * 100))
                    : 0;
                  return (
                    <tr
                      key={h.ym}
                      className={`border-t border-border/60 ${h.is_current ? 'bg-[var(--gp-gold)]/8' : ''}`}
                      data-testid={`history-row-${h.ym}`}
                    >
                      <td className="py-2 px-2">
                        <div className="flex items-center gap-2">
                          <span className={h.is_current ? 'font-medium text-[var(--gp-gold)]' : ''}>
                            {h.label}
                          </span>
                          {h.is_current && (
                            <Badge variant="outline" className="text-[9px] py-0 h-4 border-[var(--gp-gold)]/40 text-[var(--gp-gold)]">
                              {t('income.inProgress', 'in corso')}
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="py-2 px-2 text-right tabular-nums">
                        <div className="flex items-center gap-2 justify-end">
                          <div className="h-1.5 rounded-full bg-[var(--surface-2)] w-16 overflow-hidden hidden sm:block">
                            <div
                              className="h-full bg-[var(--gp-gold)]"
                              style={{ width: `${barPct}%` }}
                            />
                          </div>
                          <span>{fmtEUR(h.matured_total)}</span>
                        </div>
                      </td>
                      <td className="py-2 px-2 text-right tabular-nums text-emerald-700">
                        {fmtEUR(h.collected_total)}
                      </td>
                      <td className="py-2 px-2 text-right tabular-nums text-muted-foreground">
                        {h.count || 0}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="mt-4 text-right">
        <Link
          to="/app/my-commissions"
          className="text-xs text-[hsl(var(--primary))] hover:underline inline-flex items-center gap-1"
          data-testid="view-all-commissions-link"
        >
          Vai a tutte le mie commissioni <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
    </Card>
  );
}

function InvoiceDeadlineRow({ row }) {
  const daysRem = row.days_remaining;
  let barClass = 'bg-blue-100 text-blue-800 border-blue-200';
  let badgeText = i18n.t('income.nDays', '{{n}} giorni', { n: daysRem });
  let Icon = Clock;

  if (row.is_overdue) {
    barClass = 'bg-red-100 text-red-800 border-red-300';
    badgeText = daysRem === 0 ? i18n.t('income.today', 'oggi!') : i18n.t('income.overdueBy', 'scaduta da {{n}}gg', { n: Math.abs(daysRem) });
    Icon = AlertTriangle;
  } else if (row.is_urgent) {
    barClass = 'bg-amber-100 text-amber-900 border-amber-300';
    badgeText = daysRem === 0 ? i18n.t('income.today', 'oggi!') : (daysRem === 1 ? i18n.t('income.oneDay', '1 giorno') : i18n.t('income.nDays', '{{n}} giorni', { n: daysRem }));
    Icon = AlertTriangle;
  }

  return (
    <div
      className="flex items-center justify-between gap-3 py-2 px-3 rounded-lg bg-[var(--surface-2)]/50 border border-border hover:bg-[var(--surface-2)] transition-colors"
      data-testid={`invoice-deadline-${row.id}`}
    >
      <div className="min-w-0 flex-1">
        <div className="text-sm truncate flex items-center gap-1.5">
          <FileText className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
          <span className="truncate">{row.lead_name}</span>
          {row.resort_slug && (
            <Badge variant="outline" className="text-[9px] py-0 h-4">
              {row.resort_slug}
            </Badge>
          )}
        </div>
        <div className="text-[11px] text-muted-foreground mt-0.5">
          {i18n.t('income.collectedOn', 'Incassato il')} {fmtDate(row.paid_at)} · {i18n.t('income.deadline', 'Scadenza:')} <b className="text-foreground">{fmtDate(row.invoice_deadline)}</b>
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className="font-medium tabular-nums">{fmtEUR(row.amount)}</div>
        <Badge className={`border text-[10px] mt-0.5 ${barClass}`}>
          <Icon className="w-3 h-3 mr-1" />
          {badgeText}
        </Badge>
      </div>
    </div>
  );
}
